import { decimal } from '@snap-types/schemas/decimal';
import { z } from 'zod/v4';

import { uuid } from '../common';
import {
  alphaDimensionUnitSchema,
  AlphaQuoteEvaluationStatus,
  alphaSiteSchema,
} from '../enums';
import { base } from './base';

/**
 * Internal notes, one per operation Alpha can build. Authored on the quote line
 * because Alpha has no operation records of its own; an apply copies each note
 * onto the SnapOperation it builds, and drops the notes for operations it skips.
 */
export const operationNotes = {
  toolingNote: z.string().nullish(),
  headingNote: z.string().nullish(),
  trimmingNote: z.string().nullish(),
  pinchNote: z.string().nullish(),
  rollingNote: z.string().nullish(),
  shankSlottingNote: z.string().nullish(),
  sawSlottingNote: z.string().nullish(),
  dephosNote: z.string().nullish(),
  heatTreatNote: z.string().nullish(),
  surfaceFinishNote: z.string().nullish(),
  postFinishNote: z.string().nullish(),
  osp2Note: z.string().nullish(),
  osp3Note: z.string().nullish(),
};

export type AlphaOperationNoteKey = keyof typeof operationNotes;

export const strictInputs = {
  description: z.string().nullish(),
  descriptionIsOverridden: z.boolean(),

  // Visual: WORK_ORDER.DRAWING_ID/DRAWING_REV_NO nvarchar(30)/(8), once a snap
  // work order is built from this line - reject here rather than let an
  // oversized value fail later at Visual-send time.
  drawingId: z
    .string()
    .max(30, 'Drawing ID must be 30 characters or less')
    .nullish(),
  drawingRevisionNo: z
    .string()
    .max(8, 'Drawing Revision No must be 8 characters or less')
    .nullish(),
  notes: z.string().nullish(),
  customerPartId: z.string().nullish(),

  fastenerType: z.string(),
  material: z.string(),
  dfars: z.boolean(),
  threadSize: z.string(),
  threadPitch: z.string(),
  length: decimal,

  shoulderDiameter: decimal.nullish(),
  shoulderLength: decimal.nullish(),

  headStyle: z.string(),
  recess: z.string(),
  serration: z.boolean(),

  surfaceFinish: z.string(),
  postFinish: z.string(),

  sems1Style: z.string().nullish(),
  sems1Weight: decimal.nullish(),
  sems2Style: z.string().nullish(),
  sems2Weight: decimal.nullish(),

  osp2: z.string().nullish(),
  osp2Description: z.string().nullish(),
  osp2UOM: z.string().nullish(),
  osp2MinLot: decimal.nullish(),

  osp3: z.string().nullish(),
  osp3Description: z.string().nullish(),
  osp3UOM: z.string().nullish(),
  osp3MinLot: decimal.nullish(),

  ...operationNotes,

  hasSemsEmbed: z.boolean(),
};

export const alphaQuoteLineStrictInputsSchema = z.object(strictInputs);

export type AlphaQuoteLineStrictInputs = z.output<
  typeof alphaQuoteLineStrictInputsSchema
>;

export const strictCalculatedData = {
  calculatedDescription: z.string(),

  // --- Material and Dimensions ---
  dimensionUnit: alphaDimensionUnitSchema,
  baseMaterial: z.string(),
  thread: z.string(),
  minimumQuantity: decimal,
  convertedLength: decimal,
  convertedShoulderDiameter: decimal.nullish(),
  convertedShoulderLength: decimal.nullish(),

  preTrimHeadVolume: decimal.nullish(),
  finalHeadVolume: decimal,
  shoulderVolume: decimal,
  threadVolume: decimal,
  preTrimVolume: decimal,
  finalVolume: decimal,

  preTrimWeight: decimal,
  finalWeight: decimal,

  rawMaterialSize: decimal,
  cutoffLength: decimal,
  diametersInHead: decimal,
  threadBlankDiameter: decimal,
  reductionInArea: decimal,

  // --- Operations and Requirements ---
  toolingOperation: z.string(),

  headingOperation: z.string(),
  headingRate: decimal,
  headingSetupHours: decimal,
  headingSetupCost: decimal,
  headingSetupLaborCost: decimal,
  headingSetupBurdenCost: decimal,
  headingSetupLabor: decimal,
  headingSetupBurden: decimal,
  headingRunLabor: decimal,
  headingRunBurden: decimal,

  pinchOperation: z.string().nullish(),
  pinchRate: decimal.nullish(),
  pinchSetupHours: decimal.nullish(),
  pinchSetupCost: decimal.nullish(),
  pinchSetupLaborCost: decimal.nullish(),
  pinchSetupBurdenCost: decimal.nullish(),
  pinchSetupLabor: decimal.nullish(),
  pinchSetupBurden: decimal.nullish(),
  pinchRunLabor: decimal.nullish(),
  pinchRunBurden: decimal.nullish(),

  rollingOperation: z.string().nullish(),
  rollingRate: decimal.nullish(),
  rollingSetupHours: decimal.nullish(),
  rollingSetupCost: decimal.nullish(),
  rollingSetupLaborCost: decimal.nullish(),
  rollingSetupBurdenCost: decimal.nullish(),
  rollingSetupLabor: decimal.nullish(),
  rollingSetupBurden: decimal.nullish(),
  rollingRunLabor: decimal.nullish(),
  rollingRunBurden: decimal.nullish(),

  trimmingOperation: z.string().nullish(),
  trimmingRate: decimal.nullish(),
  trimmingSetupHours: decimal.nullish(),
  trimmingSetupCost: decimal.nullish(),
  trimmingSetupLaborCost: decimal.nullish(),
  trimmingSetupBurdenCost: decimal.nullish(),
  trimmingSetupLabor: decimal.nullish(),
  trimmingSetupBurden: decimal.nullish(),
  trimmingRunLabor: decimal.nullish(),
  trimmingRunBurden: decimal.nullish(),

  shankSlottingOperation: z.string().nullish(),
  shankSlottingRate: decimal.nullish(),
  shankSlottingSetupHours: decimal.nullish(),
  shankSlottingSetupCost: decimal.nullish(),
  shankSlottingSetupLaborCost: decimal.nullish(),
  shankSlottingSetupBurdenCost: decimal.nullish(),
  shankSlottingSetupLabor: decimal.nullish(),
  shankSlottingSetupBurden: decimal.nullish(),
  shankSlottingRunLabor: decimal.nullish(),
  shankSlottingRunBurden: decimal.nullish(),

  sawSlottingOperation: z.string().nullish(),
  sawSlottingRate: decimal.nullish(),
  sawSlottingSetupHours: decimal.nullish(),
  sawSlottingSetupCost: decimal.nullish(),
  sawSlottingSetupLaborCost: decimal.nullish(),
  sawSlottingSetupBurdenCost: decimal.nullish(),
  sawSlottingSetupLabor: decimal.nullish(),
  sawSlottingSetupBurden: decimal.nullish(),
  sawSlottingRunLabor: decimal.nullish(),
  sawSlottingRunBurden: decimal.nullish(),

  dephosRequired: z.boolean(),
  dephosServiceId: z.string().nullish(),

  heatTreatType: z.string().nullish(),
  heatTreatServiceId: z.string().nullish(),

  surfaceFinishServiceId: z.string().nullish(),
  surfaceFinishMinLot: decimal.nullish(),

  postFinishServiceId: z.string().nullish(),
  postFinishMinLot: decimal.nullish(),

  osp2ServiceId: z.string().nullish(),
  osp3ServiceId: z.string().nullish(),

  manufacturerMaterialId: z.string(),

  driveStyleAdder: decimal,
  threadFormingAdder: decimal,
  matThreadAdder: decimal,

  // --- Lead Time ---
  materialLeadTimeWeeks: z.number().int(),
  headingLeadTimeWeeks: z.number().int(),
  pinchPointLeadTimeWeeks: z.number().int().nullish(),
  rollingLeadTimeWeeks: z.number().int(),
  trimmingLeadTimeWeeks: z.number().int().nullish(),
  shankSlottingLeadTimeWeeks: z.number().int().nullish(),
  sawSlottingLeadTimeWeeks: z.number().int().nullish(),
  dephosLeadTimeWeeks: z.number().int().nullish(),
  heatTreatLeadTimeWeeks: z.number().int().nullish(),
  finishingLeadTimeWeeks: z.number().int(),
  osp2LeadTimeWeeks: z.number().int().nullish(),
  osp3LeadTimeWeeks: z.number().int().nullish(),
  orderEntryLeadTimeWeeks: z.number().int(),
  toolingLeadTimeWeeks: z.number().int(),

  manufacturerSiteCode: alphaSiteSchema,
  siteUuid: uuid,
};

export const alphaQuoteLineStrictCalculatedDataSchema =
  z.object(strictCalculatedData);

export type AlphaQuoteLineStrictCalculatedData = z.output<
  typeof alphaQuoteLineStrictCalculatedDataSchema
>;

export const strict = {
  ...base,
  ...strictInputs,
  ...strictCalculatedData,
  evaluationStatus: z.enum([AlphaQuoteEvaluationStatus.CALCULATED]),
};

export const alphaQuoteLineStrictSchema = z.object(strict);

export type AlphaQuoteLineStrict = z.output<typeof alphaQuoteLineStrictSchema>;
